import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Shared markdown renderer themed for the fleet UI (green/monospace).
// Used for agent chat replies and the in-app .md file viewer.
//
// XSS posture (#2356):
//   react-markdown v10 is safe by default — it builds a virtual DOM from a
//   remark/rehype AST and never calls dangerouslySetInnerHTML.  Raw HTML
//   blocks in markdown are silently dropped unless rehype-raw is explicitly
//   added as a plugin.  We do NOT add rehype-raw here or anywhere in this
//   codebase, so inline HTML like <script> or <img onerror=…> in user content
//   is stripped at the AST level before React ever sees it.
//   Reference: https://github.com/remarkjs/react-markdown#security
//
// Size cap: callers are responsible for enforcing a byte limit BEFORE passing
// content here (see PublicSharePage.tsx DETAIL_MAX_BYTES).  This component
// renders whatever string it receives; a 5 MB string will work but will be
// slow.  Enforce limits at the call-site for public/unauthenticated routes.
export function Markdown({ children, compact = false }: { children: string; compact?: boolean }) {
  const base = compact ? "0.78rem" : "0.85rem";
  return (
    <div style={{ fontFamily: "monospace", fontSize: base, lineHeight: 1.6, color: "var(--brand-text-link)", wordBreak: "break-word" }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p:  ({ children }) => <p style={{ margin: "0 0 0.5em" }}>{children}</p>,
          h1: ({ children }) => <h1 style={{ color: "var(--brand-text-subtle)", fontSize: "1.25em", margin: "0.6em 0 0.3em" }}>{children}</h1>,
          h2: ({ children }) => <h2 style={{ color: "var(--brand-text-secondary)", fontSize: "1.15em", margin: "0.6em 0 0.3em" }}>{children}</h2>,
          h3: ({ children }) => <h3 style={{ color: "var(--brand-text-secondary)", fontSize: "1.05em", margin: "0.5em 0 0.25em" }}>{children}</h3>,
          ul: ({ children }) => <ul style={{ margin: "0 0 0.5em", paddingLeft: "1.3em" }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ margin: "0 0 0.5em", paddingLeft: "1.3em" }}>{children}</ol>,
          li: ({ children }) => <li style={{ margin: "0.15em 0" }}>{children}</li>,
          a:  ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" style={{ color: "var(--brand-text-link)", textDecoration: "underline" }}>{children}</a>,
          strong: ({ children }) => <strong style={{ color: "var(--brand-text-subtle)" }}>{children}</strong>,
          em: ({ children }) => <em style={{ color: "var(--brand-text-secondary)" }}>{children}</em>,
          blockquote: ({ children }) => (
            <blockquote style={{ margin: "0 0 0.5em", paddingLeft: "0.8em", borderLeft: "3px solid var(--brand-border-subtle)", color: "var(--brand-text-muted)" }}>{children}</blockquote>
          ),
          hr: () => <hr style={{ border: "none", borderTop: "1px solid var(--brand-border-subtle)", margin: "0.8em 0" }} />,
          code: ({ children, className }) => {
            // Inline code (no language class) vs. fenced block.
            const isBlock = /language-/.test(className ?? "");
            if (isBlock) {
              return (
                <code style={{ display: "block", background: "var(--brand-bg-deep)", border: "1px solid var(--brand-border-subtle)", borderRadius: "var(--brand-radius-sm)", padding: "8px 10px", margin: "0 0 0.5em", overflowX: "auto", whiteSpace: "pre", color: "var(--brand-text-secondary)", fontSize: "0.92em" }}>
                  {children}
                </code>
              );
            }
            return <code style={{ background: "var(--brand-bg-deep)", border: "1px solid var(--brand-border-subtle)", borderRadius: 3, padding: "1px 5px", fontSize: "0.92em", color: "var(--brand-text-subtle)" }}>{children}</code>;
          },
          pre: ({ children }) => <pre style={{ margin: 0 }}>{children}</pre>,
          table: ({ children }) => (
            <div style={{ overflowX: "auto", marginBottom: "0.5em" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>{children}</table>
            </div>
          ),
          th: ({ children }) => <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid var(--brand-border-strong)", color: "var(--brand-text-subtle)" }}>{children}</th>,
          td: ({ children }) => <td style={{ padding: "4px 8px", borderBottom: "1px solid var(--brand-border-subtle)", verticalAlign: "top" }}>{children}</td>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
